import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { QrCode, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 2026";
const CONTACT_EMAIL = "support@commonqr.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>Privacy Policy — CommonQR</title>
        <meta name="description" content="CommonQR Privacy Policy — how we collect, use, and protect your data." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header */}
      <div className="border-b border-border sticky top-0 bg-card/95 backdrop-blur-sm z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <a className="inline-flex items-center gap-2 group">
              <QrCode className="w-6 h-6 text-indigo-600" />
              <span className="font-bold text-lg text-foreground group-hover:text-indigo-600 transition-colors">
                CommonQR
              </span>
            </a>
          </Link>
          <Link href="/">
            <a className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </a>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Title */}
        <div className="mb-10">
          <p className="text-sm font-medium text-indigo-600 mb-2">Legal</p>
          <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
          <p className="text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
          <p className="text-muted-foreground mt-1">Operated by: Vigneswaran S, trading as CommonQR</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">

          {/* Intro */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6">
            <p className="text-foreground/80 leading-relaxed">
              CommonQR ("we", "us", "our") operates the website commonqr.com and the CommonQR platform.
              This Privacy Policy explains what information we collect, how we use it, and your rights
              regarding your data. We are committed to protecting your privacy and handling your data
              with transparency and respect.
            </p>
          </div>

          <Section title="1. Who We Are">
            <p>
              CommonQR is a QR profile platform that allows individuals and businesses to create a
              permanent digital identity accessible via a QR code. The platform is operated by
              Vigneswaran S, trading as CommonQR. A formal legal entity registration is in progress.
              Until that registration is complete, all legal obligations under this policy are the
              personal responsibility of Vigneswaran S.
            </p>
            <p className="mt-3">
              Contact us at: <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>
            </p>
          </Section>

          <Section title="2. What Data We Collect">
            <SubSection title="2.1 Account Data">
              <p>When you create a CommonQR account, we collect:</p>
              <ul>
                <li>Your name and email address</li>
                <li>Password (stored as a secure bcrypt hash — never in plain text)</li>
                <li>Your chosen username (permanent — becomes your QR profile URL)</li>
                <li>Profile information you choose to add: display name, bio, location, website, phone, avatar image</li>
                <li>If you sign up via Google OAuth: your name, email, and Google profile photo</li>
              </ul>
            </SubSection>

            <SubSection title="2.2 QR Scan Data">
              <p>Every time your QR code is scanned, we record:</p>
              <ul>
                <li>Date and time of the scan</li>
                <li>Device type (mobile, desktop, tablet) — derived from the user agent string</li>
                <li>Country and city — derived from the IP address using geolocation</li>
                <li>Referring URL if available</li>
              </ul>
              <p className="mt-2">
                We do not store the full IP address of the person scanning your QR code beyond what is
                required for geolocation lookup. Scan data is used to provide analytics to the profile owner.
              </p>
            </SubSection>

            <SubSection title="2.3 Payment Data">
              <p>
                If you upgrade to a paid plan, payments are processed by Stripe. CommonQR does not
                store your card number, CVV, or any payment credentials. Stripe's privacy policy
                governs the handling of your payment information. We store only your subscription
                status, plan type, and Stripe customer ID.
              </p>
            </SubSection>

            <SubSection title="2.4 Usage Data">
              <p>We automatically collect basic usage data including:</p>
              <ul>
                <li>Pages visited on commonqr.com</li>
                <li>Browser type and operating system</li>
                <li>Referring website</li>
                <li>Session duration</li>
              </ul>
              <p className="mt-2">This data is used to improve the platform and is not sold to any third party.</p>
            </SubSection>
          </Section>

          <Section title="3. How We Use Your Data">
            <Table
              headers={["Purpose", "Legal Basis"]}
              rows={[
                ["To create and manage your account", "Contract performance"],
                ["To generate and serve your QR profile page", "Contract performance"],
                ["To provide scan analytics to profile owners", "Contract performance"],
                ["To process subscription payments", "Contract performance"],
                ["To send transactional emails (verification, password reset, renewal reminders)", "Contract performance"],
                ["To improve the platform and fix bugs", "Legitimate interest"],
                ["To prevent fraud and abuse", "Legitimate interest"],
                ["To comply with legal obligations", "Legal obligation"],
              ]}
            />
            <p className="mt-4 text-muted-foreground">
              We do not use your data for advertising. CommonQR does not show ads and does not sell
              your data to advertisers or any third party.
            </p>
          </Section>

          <Section title="4. Public Profile Data">
            <p>
              Your CommonQR profile at <strong>commonqr.com/username</strong> is public by default.
              This means the following information is visible to anyone who visits your profile URL
              or scans your QR code:
            </p>
            <ul className="mt-3">
              <li>Display name</li>
              <li>Bio</li>
              <li>Avatar image (if uploaded)</li>
              <li>Location (if provided)</li>
              <li>Website (if provided)</li>
              <li>Links you have added to your profile</li>
              <li>Total scan count</li>
            </ul>
            <p className="mt-3">
              You can set your profile to private in your dashboard settings. A private profile
              will not be accessible to the public but your QR code URL will remain reserved.
            </p>
          </Section>

          <Section title="5. Data Sharing">
            <p>We share your data only with the following third-party services, strictly for platform operation:</p>
            <Table
              headers={["Service", "Purpose", "Their Privacy Policy"]}
              rows={[
                ["Stripe", "Payment processing for paid plans", "stripe.com/privacy"],
                ["Gmail / Google SMTP", "Sending transactional emails", "policies.google.com/privacy"],
                ["Google OAuth", "Sign in with Google (optional)", "policies.google.com/privacy"],
                ["Hostinger VPS", "Server infrastructure hosting", "hostinger.com/privacy-policy"],
              ]}
            />
            <p className="mt-4">
              We do not share your data with any other third parties. We do not sell, rent, or
              trade your personal information.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <ul>
              <li><strong>Account data:</strong> Retained for as long as your account is active. If you delete your account, your data is deleted within 30 days.</li>
              <li><strong>Scan logs:</strong> Retained for 24 months from the date of the scan, then permanently deleted.</li>
              <li><strong>Payment records:</strong> Retained for 7 years as required by financial regulations.</li>
              <li><strong>Backup data:</strong> Encrypted database backups are retained for 30 days then deleted.</li>
            </ul>
          </Section>

          <Section title="7. Cookies">
            <p>CommonQR uses a minimal set of cookies:</p>
            <Table
              headers={["Cookie", "Purpose", "Duration"]}
              rows={[
                ["commonqr_token", "Authentication — keeps you logged in", "7 days"],
                ["Session cookie", "Maintains your session while using the dashboard", "Session"],
              ]}
            />
            <p className="mt-4">
              We do not use advertising cookies, tracking pixels, or third-party analytics cookies.
              We do not use Google Analytics or any similar tracking service.
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>Regardless of your location, you have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> You can request a copy of all personal data we hold about you.</li>
              <li><strong>Correction:</strong> You can update your profile data at any time from your dashboard.</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and all associated data by emailing us.</li>
              <li><strong>Portability:</strong> Pro Plus users can export their scan data as CSV. Account data export is available on request.</li>
              <li><strong>Objection:</strong> You can object to processing of your data for legitimate interest purposes.</li>
              <li><strong>Withdrawal of consent:</strong> Where processing is based on consent, you can withdraw it at any time.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
              We will respond within 30 days.
            </p>
          </Section>

          <Section title="9. Data Security">
            <p>We take data security seriously. Our measures include:</p>
            <ul>
              <li>All data transmitted over HTTPS with TLS encryption</li>
              <li>Passwords hashed using bcrypt — never stored in plain text</li>
              <li>JWT tokens with 7-day expiry for authentication</li>
              <li>PostgreSQL database on a private network — not publicly accessible</li>
              <li>Daily automated database backups with encryption</li>
              <li>Access to production systems restricted to the operator</li>
            </ul>
            <p className="mt-3">
              No system is 100% secure. If you discover a security vulnerability, please report it
              responsibly to <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
            </p>
          </Section>

          <Section title="10. Children's Privacy">
            <p>
              CommonQR is not directed at children under the age of 13. We do not knowingly collect
              personal data from children under 13. If you believe a child under 13 has provided us
              with personal data, please contact us and we will delete it promptly.
            </p>
          </Section>

          <Section title="11. International Users">
            <p>
              CommonQR is operated from India and serves users globally. If you are located in the
              European Union, United Kingdom, or other jurisdictions with specific data protection
              laws, we process your data in a manner consistent with the principles of those laws —
              including lawful basis for processing, data minimisation, and your rights of access
              and deletion — even though we are not currently established in those jurisdictions.
            </p>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes,
              we will notify registered users by email and update the effective date at the top of
              this page. Your continued use of CommonQR after the effective date constitutes
              acceptance of the updated policy.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>For any privacy-related questions, requests, or concerns:</p>
            <div className="mt-3 bg-muted rounded-xl p-5 border border-border">
              <p className="font-semibold text-foreground">CommonQR</p>
              <p className="text-muted-foreground">Operated by Vigneswaran S</p>
              <p className="text-muted-foreground mt-1">
                Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p className="text-muted-foreground">Website: commonqr.com</p>
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© 2026 CommonQR. All rights reserved.</p>
          <div className="flex gap-6">
            <Link href="/terms">
              <a className="text-sm text-muted-foreground hover:text-foreground/80 transition-colors">Terms of Service</a>
            </Link>
            <Link href="/privacy">
              <a className="text-sm text-indigo-600 font-medium">Privacy Policy</a>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Helper components ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-muted">
            {headers.map((h) => (
              <th key={h} className="text-left px-4 py-3 font-semibold text-foreground/80 border border-border">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-muted/50"}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-muted-foreground border border-border">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}